import moment from "moment";

import { breakTimeIntoXMinuteIntervals } from "@/utils/calendar/dates";
import { getLocalTimezone } from "@/redux/reducers/authReducer";
import { transformTimeSlotArray } from "@/components/Calendar/CalendarAppointmentPicker/calendarAppointmentPickerUtils";

// Legacy radio uses "True/False" string as value, needs to be cast before submission
export const ACP_NOTE_FIELDS_CAST_TO_BOOLEAN = [
  "active_substance",
  "activity_result",
  "acute_episode",
  "activity_difficulties",
  "patient_encounter",
  "patient_participant_attest",
  "sdoh",
];

export const ADVANCED_CARE_ACTIVITY_INITIAL_VALUES = {
  content: {
    active_substance: "",
    active_substance_details: "",
    activity_purpose: [],
    activity_purpose_other_details: "",
    activity_reason: "",
    activity_result: "", // converted to boolean on submission
    activity_result_note: "",
    activity_service_type: "",
    acute_episode: "",
    acute_episode_details: "",
    additional_information: "",
    care_team_member_type: "",
    care_team_member_type_other_details: "",
    communication_type: "",
    communication_type_other_details: "",
    activity_difficulties: "",
    activity_difficulties_details: "",
    intended_duration: "",
    patient_encounter: "", // converted to boolean on submission
    patient_participant_attest: "", // converted to boolean on submission
    reason_for_communication: [],
    recommended_next: [],
    recommended_next_other_details: "",
    sdoh: "",
    sdoh_details: "",
    timezone: getLocalTimezone(),
  },
  is_billable: false,
  is_processed: false,
  note_state: "completed",
  note_type: "Advanced Care Activity",
  patient_first_name: "",
  patient_last_name: "",
  provider_cp_credentials: "",
  provider_id: "",
  provider_name: "",
  provider_type: "",
  service_location: "Virtual",
  session_date: moment().format("MMDDYYYY"),
  session_from: "",
  session_to: "",
  signature: "",
  signature_date: "",
  title: "Advanced Care Activity Assessment",
  version: 7,
};

export const ACTIVITY_SEVICE_TYPE_OPTIONS = [
  {
    label: "Case management",
    value: "Case management",
  },
  {
    label: "Peer support",
    value: "Peer support",
  },
  {
    label: "Care coordination",
    value: "Care coordination",
  },
  {
    label: "Prescriber support",
    value: "Prescriber support",
  },
];

export const ACTIVITY_PURPOSE_OPTIONS = [
  {
    label: "Crisis support",
    value: "Crisis support",
  },
  {
    label: "Goal support",
    value: "Goal support",
  },
  {
    label: "Medication management support",
    value: "Medication management support",
  },
  {
    label: "Patient education",
    value: "Patient education",
  },
  {
    label: "Patient outreach",
    value: "Patient outreach",
  },
  {
    label: "Real-time event notification",
    value: "Real-time event notification",
  },
  {
    label: "Quality measure support",
    value: "Quality measure support",
  },
  {
    label: "Scheduling support",
    value: "Scheduling support",
  },
  {
    label: "Other (Please Specify)",
    value: "Other",
  },
  {
    label: "Translation services",
    value: "Translation services",
  },
  { label: "HARP education", value: "HARP education" },
];

export const ACTIVITY_CLASS_OPTIONS = [
  {
    label: "Virtual",
    value: "Virtual",
  },
  {
    label: "In Person",
    value: "In Person",
  },
];

export const COMMUNICATION_TYPE_OPTIONS = [
  {
    label: "Phone call (audio only)",
    value: "Phone call (audio only)",
  },
  {
    label: "Video call (audio and video)",
    value: "Video call (audio and video)",
  },
  {
    label: "Text",
    value: "Text",
  },
  {
    label: "In app messaging",
    value: "In app messaging",
  },
  {
    label: "On call",
    value: "On call",
  },
  {
    label: "In person patient home",
    value: "In person patient home",
  },
  {
    label: "In person office",
    value: "In person office",
  },
  {
    label: "In person transportation",
    value: "In person transportation",
  },
  {
    label: "Other (Please Specify)",
    value: "Other",
  },
];

export const ACTIVITY_REASON_OPTIONS = [
  {
    label: "Yes, planned/routine",
    value: "Yes, planned/routine",
  },
  {
    label: "No, unplanned",
    value: "No, unplanned",
  },
];

export const CARE_TEAM_MEMBER_TYPE_OPTIONS = [
  {
    label: "BHS",
    value: "BHS",
  },
  {
    label: "Prescriber",
    value: "Prescriber",
  },
  {
    label: "RN Case Manager",
    value: "RN Case Manager",
  },
  {
    label: "RN (prescriber support)",
    value: "RN (prescriber support)",
  },
  {
    label: "CMA (Certified Medical Assistant)",
    value: "CMA (Certified Medical Assistant)",
  },
  {
    label: "Peer Specialist",
    value: "Peer Specialist",
  },
  {
    label: "Care Coordinator",
    value: "Care Coordinator",
  },
  {
    label: "Clinical Supervisor",
    value: "Clinical Supervisor",
  },
  {
    label: "Other (Please Specify)",
    value: "Other",
  },
];

export const YES_NO_OPTIONS = [
  { label: "Yes", value: "Yes", booleanValue: true },
  { label: "No", value: "No", booleanValue: false },
];

export const REASON_FOR_COMMUNICATION_OPTIONS = [
  { label: "Appointment changes", value: "Appointment changes" },
  { label: "SUD", value: "SUD" },
  { label: "SDoH", value: "SDoH" },
  { label: "Medical", value: "Medical" },
  { label: "Acute Care Encounter", value: "Acute Care Encounter" },
  { label: "Outreach/support", value: "Outreach/support" },
  { label: "Medication", value: "Medication" },
  { label: "Care team collaboration", value: "Care team collaboration" },
  { label: "CBO connection", value: "CBO connection" },
];

export const RECOMMENDED_NEXT_OPTIONS = [
  {
    label: "Follow up within 24-48 hours (immediate need)",
    value: "Follow up within 24-48 hours (immediate need)",
  },
  { label: "Follow up within week", value: "Follow up within week" },
  { label: "Follow up within month", value: "Follow up within month" },
  { label: "No follow up needed at this time", value: "No follow up needed at this time" },
  { label: "Other (Please Specify)", value: "Other" },
];

export const TIME_SLOT_OPTIONS = transformTimeSlotArray(
  breakTimeIntoXMinuteIntervals(moment("00:00", "HH:mm"), moment("23:59", "HH:mm"), 5),
);

const INTENDED_DURATION_MAX_LENGTH_MINUTES = 120;
const INTENDED_DURATION_INCREMENT = 5;

export const INTENDED_DURATION_OPTIONS = Array.from(
  Array(INTENDED_DURATION_MAX_LENGTH_MINUTES / INTENDED_DURATION_INCREMENT),
  (_, index) => {
    const numberOfMinutes = (index + 1) * 5;
    const duration = moment.duration(numberOfMinutes, "minutes");
    const hours = duration.hours();
    const remainingMinutes = duration.minutes();

    let formattedString = "";
    if (hours === 0) {
      formattedString = `${remainingMinutes} min`;
    } else if (remainingMinutes === 0) {
      formattedString = `${hours} hr`;
    } else {
      formattedString = `${hours} hr ${remainingMinutes} min`;
    }

    return {
      label: formattedString,
      value: numberOfMinutes,
    };
  },
);

export const filterDurationOptionsByLabel = (option, inputValue) => {
  return option.label.toLowerCase().includes(inputValue.toLowerCase());
};

// Configuration for conditional field clearing in onSubmit
// Each rule defines a details field and the condition under which it should be cleared
export const CONDITIONAL_FIELD_CLEARING_RULES = [
  {
    detailsField: "activity_purpose_other_details",
    shouldClear: (content) => !content.activity_purpose.includes("Other"),
  },
  {
    detailsField: "communication_type_other_details",
    shouldClear: (content) => content.communication_type !== "Other",
  },
  {
    detailsField: "care_team_member_type_other_details",
    shouldClear: (content) => content.care_team_member_type !== "Other",
  },
  {
    detailsField: "activity_result_note",
    shouldClear: (content) => content.activity_result !== "No",
  },
  {
    detailsField: "recommended_next_other_details",
    shouldClear: (content) => !content.recommended_next.includes("Other"),
  },
  {
    detailsField: "active_substance_details",
    shouldClear: (content) => content.active_substance === "No",
  },
  {
    detailsField: "acute_episode_details",
    shouldClear: (content) => content.acute_episode === "No",
  },
  {
    detailsField: "activity_difficulties_details",
    shouldClear: (content) => content.activity_difficulties === "No",
  },
  {
    detailsField: "sdoh_details",
    shouldClear: (content) => content.sdoh === "No",
  },
];
